import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useAuth } from '../modules/auth/core/Auth'
import { logger } from '../utils/logger'

// Session configuration
const SESSION_CONFIG = {
  INACTIVITY_TIMEOUT: 4 * 60 * 60 * 1000, // 4 hours in milliseconds
  ABSOLUTE_TIMEOUT: 7 * 24 * 60 * 60 * 1000, // 1 week in milliseconds
  WARNING_BEFORE: 5 * 60 * 1000, // 5 minutes before logout
  CHECK_INTERVAL: 30 * 1000, // Check every 30 seconds
}

const STORAGE_KEYS = {
  LAST_ACTIVITY: 'session_last_activity',
  SESSION_START: 'session_start_time',
}

const SessionManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, logout } = useAuth()
  const [showWarning, setShowWarning] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [warningType, setWarningType] = useState<'inactivity' | 'absolute'>('inactivity')
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    if (currentUser) {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString())
    }
  }, [currentUser])

  // Initialize session on login
  useEffect(() => {
    if (currentUser) {
      const sessionStart = localStorage.getItem(STORAGE_KEYS.SESSION_START)
      if (!sessionStart) {
        localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString())
      }
      updateActivity()
    }
  }, [currentUser, updateActivity])

  // Listen for user activity
  useEffect(() => {
    if (!currentUser) return

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    
    const handleActivity = () => {
      updateActivity()
      // If warning is showing due to inactivity, hide it
      if (showWarning && warningType === 'inactivity') {
        setShowWarning(false)
      }
    }

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [currentUser, updateActivity, showWarning, warningType])

  // Check session status periodically
  useEffect(() => {
    if (!currentUser) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
      return
    }

    const checkSession = () => {
      const now = Date.now()
      const lastActivity = parseInt(localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY) || '0')
      const sessionStart = parseInt(localStorage.getItem(STORAGE_KEYS.SESSION_START) || '0')

      const timeSinceActivity = now - lastActivity
      const sessionDuration = now - sessionStart

      // Check absolute timeout first (takes priority)
      const timeUntilAbsoluteExpiry = SESSION_CONFIG.ABSOLUTE_TIMEOUT - sessionDuration
      if (timeUntilAbsoluteExpiry <= 0) {
        handleLogout('absolute_timeout')
        return
      }

      // Check inactivity timeout
      const timeUntilInactivityExpiry = SESSION_CONFIG.INACTIVITY_TIMEOUT - timeSinceActivity
      if (timeUntilInactivityExpiry <= 0) {
        handleLogout('inactivity_timeout')
        return
      }

      // Show warning if approaching either timeout
      if (timeUntilAbsoluteExpiry <= SESSION_CONFIG.WARNING_BEFORE && !showWarning) {
        setWarningType('absolute')
        setTimeRemaining(Math.floor(timeUntilAbsoluteExpiry / 1000))
        setShowWarning(true)
      } else if (timeUntilInactivityExpiry <= SESSION_CONFIG.WARNING_BEFORE && !showWarning) {
        setWarningType('inactivity')
        setTimeRemaining(Math.floor(timeUntilInactivityExpiry / 1000))
        setShowWarning(true)
      }
    }

    checkSession() // Check immediately
    checkIntervalRef.current = setInterval(checkSession, SESSION_CONFIG.CHECK_INTERVAL)

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
    }
  }, [currentUser, showWarning])

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
      return
    }

    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleLogout(warningType === 'absolute' ? 'absolute_timeout' : 'inactivity_timeout')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    }
  }, [showWarning, warningType])

  const handleLogout = async (reason: string) => {
    logger.info(`Session expired: ${reason}`, {
      source: 'SessionManager.handleLogout',
      module: 'Authentication',
      action: 'SESSION_EXPIRED',
      userId: currentUser?.id
    })

    // Clear session storage
    localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY)
    localStorage.removeItem(STORAGE_KEYS.SESSION_START)

    setShowWarning(false)
    await logout()
  }

  const handleExtendSession = () => {
    // Reset inactivity timer
    updateActivity()
    
    // For absolute timeout, we can't extend - they must re-login
    if (warningType === 'absolute') {
      // Can't extend absolute timeout
      return
    }

    setShowWarning(false)
    
    logger.info('User extended session', {
      source: 'SessionManager.handleExtendSession',
      module: 'Authentication',
      action: 'SESSION_EXTENDED',
      userId: currentUser?.id
    })
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Clear session data on logout
  useEffect(() => {
    if (!currentUser) {
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY)
      localStorage.removeItem(STORAGE_KEYS.SESSION_START)
      setShowWarning(false)
    }
  }, [currentUser])

  return (
    <>
      {children}
      
      {/* Session Timeout Warning Modal */}
      {showWarning && currentUser && (
        <div 
          className='modal fade show d-block' 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex={-1}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header border-0 pb-0'>
                <h5 className='modal-title'>
                  <i className='bi bi-exclamation-triangle-fill text-warning me-2'></i>
                  Session Expiring
                </h5>
              </div>
              <div className='modal-body text-center py-8'>
                <div className='fs-1 fw-bold text-danger mb-4'>
                  {formatTime(timeRemaining)}
                </div>
                <p className='text-gray-600 fs-5 mb-0'>
                  {warningType === 'inactivity' ? (
                    <>
                      Your session will expire due to inactivity.
                      <br />
                      Click below to stay signed in.
                    </>
                  ) : (
                    <>
                      Your session will expire for security reasons.
                      <br />
                      You will need to sign in again.
                    </>
                  )}
                </p>
              </div>
              <div className='modal-footer border-0 pt-0 justify-content-center gap-3'>
                {warningType === 'inactivity' && (
                  <button 
                    className='btn btn-primary btn-lg px-8'
                    onClick={handleExtendSession}
                  >
                    Stay Signed In
                  </button>
                )}
                <button 
                  className='btn btn-light btn-lg px-8'
                  onClick={() => handleLogout('user_initiated')}
                >
                  Sign Out Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SessionManager
